/**
 * A Wireline & Slickline ticket's sign-off — the office, then the company man.
 *
 *   draft / customer_rejected   the office approves. `ticket_unpriced` is said
 *                               in a sentence AND names the lines.
 *   office_approved             send it to the company man (the recipient is
 *                               prefilled from GET /recipient) — or record the
 *                               photo of the signed paper ticket.
 *   customer_approved           who approved it, when, how, the stamp, and the
 *                               stamped file. Read-only: a recorded approval is
 *                               permanent (`approval_already_recorded`).
 *   void                        nothing to press; who voided it and why is
 *                               said on the ticket. An approval recorded
 *                               before the void is still shown.
 *
 * The review link is shown after a send, AND after a 502 `email_send_failed`
 * (its body still carries `data.reviewUrl`) — the email failing does not stop
 * the office copying the link to the company man. Every refusal is drawn in the
 * refusal style, never as a success. The review page and the stamp are the
 * casing tickets' (TicketReviewPage, lib/approval-stamp) — not a second flow.
 *
 * The photo of the signed paper ticket goes through the PRESIGNED flow
 * (`wl_ticket` uploads, useWlTickets uploadWlTicketFile), then a JSON POST
 * naming the upload id — no multipart body, so no Content-Type is ever set
 * by hand.
 */
import React, { useState } from 'react';
import { CheckCircle2, Copy, FileText, Send, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { api, getBlobApiErrorMessage } from '../../../api/client';
import { useOfficeApproveWlTicket, useWlTicketRecipient, useRecordWlTicketApproval, useSendWlTicket } from '../../../hooks/useWlTickets';
import { frozenStampDisplay, frozenStampFromTicket } from '../../../lib/approval-stamp';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../../lib/blob-tab';
import { formatInstant } from '../../../lib/calendar-date';
import { queryLoadFailure } from '../../../lib/load-failure';
import { WL_APPROVAL_SOURCE_TEXT, wlTicketRefusal, reviewUrlFrom, unpricedLinesFrom } from '../../../lib/wireline-slickline-tickets';
import type { WlTicket } from '../../../types/wireline-slickline-tickets';
import { CcField, parseCcInput } from '../../downhole/sendBits';
import { tenantLocalInput } from '../../downhole/RunDialogs';
import { WL_DATE_INPUT, WL_INPUT, WL_PRIMARY, WL_SECONDARY, WlField, WlFormError, WlPanel, WlSpec } from '../WlBits';
import { PfNote as WlNote } from '../../pipeline-facility/digs/DigBits';

// wl_tickets.customer_email_status (0591 CHECK) in words.
const EMAIL_STATUS_LABELS: Record<string, string> = {
  not_sent: 'Not sent', pending: 'Sending', sent: 'Sent', failed: 'Failed', bounced: 'Bounced',
  delivered: 'Delivered', complained: 'Marked as spam', opened: 'Opened', clicked: 'Link clicked',
};

const STAMP_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const STAMP_MAX = 20 * 1024 * 1024;

const at = (v: string | null | undefined, tz: string | null) => (v ? formatInstant(v, tz) ?? '—' : '—');

/** The review link, with a copy button. Shown after a send and after a failed email. */
export function WlReviewLink({ url }: { url: string }) {
  return (
    <div data-testid="wl-ticket-review-url" className="space-y-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-4 py-3">
      <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Review link for the company man</p>
      <p className="break-all font-mono text-xs text-white">{url}</p>
      <button
        type="button"
        className={WL_SECONDARY}
        onClick={() => {
          navigator.clipboard?.writeText(url).then(
            () => toast.success('Review link copied'),
            () => toast.error('The link could not be copied. Select it and copy it by hand.'),
          );
        }}
      >
        <Copy className="size-4" aria-hidden="true" />
        Copy link
      </button>
    </div>
  );
}

export function WlOfficeApprove({ ticket, onUnpriced }: { ticket: WlTicket; onUnpriced: (ids: string[]) => void }) {
  const approve = useOfficeApproveWlTicket();
  const [error, setError] = useState<string | null>(null);
  const [unpriced, setUnpriced] = useState<{ id: string; description: string }[]>([]);
  return (
    <div className="space-y-3 px-4 py-4" data-testid="wl-ticket-office-approve">
      <p className="text-sm text-fieldiq-text">
        {ticket.status === 'customer_rejected'
          ? 'The customer rejected this ticket. Correct it, then approve it again as the office before sending it back.'
          : 'When the lines are right, the office approves the ticket. That fixes the subtotal, and the ticket can then go to the company man.'}
      </p>
      <WlFormError message={error} />
      {unpriced.length > 0 && (
        <ul data-testid="wl-ticket-unpriced-list" className="list-disc space-y-1 rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 py-2 pl-8 pr-3 text-sm text-fieldiq-text">
          {unpriced.map((l) => (
            <li key={l.id}>{l.description}</li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className={WL_PRIMARY}
        disabled={approve.isPending}
        onClick={() => {
          setError(null);
          setUnpriced([]);
          onUnpriced([]);
          approve.mutate(ticket.id, {
            onSuccess: () => toast.success(`${ticket.ticketNumber} approved by the office`),
            onError: (e) => {
              setError(wlTicketRefusal(e, 'The ticket could not be approved.'));
              const lines = unpricedLinesFrom(e);
              setUnpriced(lines);
              onUnpriced(lines.map((l) => l.id));
            },
          });
        }}
      >
        <CheckCircle2 className="size-4" aria-hidden="true" />
        {approve.isPending ? 'Approving…' : 'Approve as the office'}
      </button>
    </div>
  );
}

export function WlSendTicket({ ticket, tz }: { ticket: WlTicket; tz: string | null }) {
  const recipient = useWlTicketRecipient(ticket.id, ticket.status === 'office_approved');
  const send = useSendWlTicket();
  const [email, setEmail] = useState('');
  const [cc, setCc] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reviewUrl, setReviewUrl] = useState<string | null>(null);
  const r = recipient.data?.data ?? null;
  const ccParsed = parseCcInput(cc);
  const failure = queryLoadFailure(recipient);

  return (
    <div className="space-y-3 px-4 py-4" data-testid="wl-ticket-send">
      <p className="text-sm text-fieldiq-text">
        Send the ticket to the company man. He opens the review link on his phone, checks the lines and signs it. The link works for 7 days.
      </p>
      <div data-testid="wl-ticket-recipient" className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
        {recipient.isLoading ? (
          'Finding who to send it to…'
        ) : failure ? (
          'Who to send it to could not be read. Enter the email below.'
        ) : r?.to ? (
          <>
            To <span className="font-bold text-white">{r.to.name ? `${r.to.name} <${r.to.email}>` : r.to.email}</span>
            {r.to.sourceLabel ? <span className="text-fieldiq-muted"> — {r.to.sourceLabel}</span> : null}
          </>
        ) : (
          <span>{r?.message ?? "This customer has no email address on file. Enter the company man's email to send the ticket."}</span>
        )}
        {r?.lastSent && (
          <p className="mt-1 text-xs text-fieldiq-muted">
            Last sent {at(r.lastSent.at, tz)} to {r.lastSent.to ?? '—'}
          </p>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <WlField label={r?.to ? 'Send to a different email' : 'Company man’s email *'} hint={r?.to ? 'Optional. Leave blank to use the address above.' : undefined}>
          <input className={WL_INPUT} type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </WlField>
        <CcField value={cc} onChange={setCc} problem={cc.trim() ? ccParsed.problem : null} />
      </div>
      <WlFormError message={error} />
      {reviewUrl && <WlReviewLink url={reviewUrl} />}
      <button
        type="button"
        className={WL_PRIMARY}
        disabled={send.isPending}
        onClick={() => {
          setError(null);
          if (cc.trim() && ccParsed.problem) return setError(ccParsed.problem);
          if (!email.trim() && !r?.to) return setError("Enter the company man's email to send the ticket.");
          send.mutate(
            { id: ticket.id, email: email.trim() || undefined, cc: cc.trim() ? ccParsed.cc : undefined },
            {
              onSuccess: (res) => {
                setReviewUrl(res.reviewUrl ?? null);
                toast.success(`${ticket.ticketNumber} sent to ${res.sentTo}`);
              },
              onError: (e) => {
                setError(wlTicketRefusal(e, 'The ticket could not be sent.'));
                setReviewUrl(reviewUrlFrom(e));
              },
            },
          );
        }}
      >
        <Send className="size-4" aria-hidden="true" />
        {send.isPending ? 'Sending…' : ticket.sentToCustomerAt ? 'Send again' : 'Send to the company man'}
      </button>
    </div>
  );
}

export function WlRecordApproval({ ticket, tz, today }: { ticket: WlTicket; tz: string | null; today: string | null }) {
  const record = useRecordWlTicketApproval();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [day, setDay] = useState(today ?? '');
  const [error, setError] = useState<string | null>(null);
  const officeDay = tenantLocalInput(ticket.officeApprovedAt, tz).slice(0, 10);

  if (!open) {
    return (
      <div className="space-y-2 px-4 py-4" data-testid="wl-ticket-record-approval">
        <p className="text-sm text-fieldiq-text">Signed on paper instead? Record the photo of the signed ticket.</p>
        <button type="button" className={WL_SECONDARY} onClick={() => { setError(null); setOpen(true); }}>
          <Upload className="size-4" aria-hidden="true" />
          Record the signed paper ticket
        </button>
      </div>
    );
  }

  const submit = () => {
    setError(null);
    if (!file) return setError('Attach the photo of the signed ticket.');
    if (!STAMP_TYPES.includes(file.type)) return setError('Attach a PDF, PNG or JPEG file.');
    if (file.size > STAMP_MAX) return setError('The file is larger than 20 MB.');
    if (!name.trim()) return setError('Enter who signed the ticket.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return setError('Enter the day the ticket was signed.');
    if (today && day > today) return setError("The day it was signed can't be in the future.");
    if (officeDay && day < officeDay) return setError("The day it was signed can't be before the office approved the ticket.");
    // Presigned upload, then JSON { upload_id, approved_by_name, approved_on } — no multipart, no hand-set Content-Type.
    record.mutate(
      { id: ticket.id, file, approvedByName: name.trim(), approvedOn: day },
      {
        onSuccess: () => {
          toast.success(`${ticket.ticketNumber} recorded as approved by ${name.trim()}`);
          setOpen(false);
        },
        // An upload step that failed throws a plain Error with its own sentence; an API refusal reads from the map.
        onError: (e) =>
          setError(
            (e as { response?: unknown })?.response
              ? wlTicketRefusal(e, 'The approval was not recorded.')
              : (e as { userMessage?: string })?.userMessage || (e instanceof Error && e.message) || 'The signed ticket could not be uploaded. Try again.',
          ),
      },
    );
  };

  return (
    <div className="space-y-3 border-t border-fieldiq-border px-4 py-4" data-testid="wl-ticket-record-approval-form">
      <p className="text-sm text-fieldiq-text">
        The photo is kept with the ticket and stamped on its PDF. <span className="font-bold text-white">This is permanent: a recorded approval cannot be replaced or removed.</span>
      </p>
      <WlFormError message={error} />
      <div className="grid gap-3 sm:grid-cols-3">
        <WlField label="Signed ticket *" hint="PDF, PNG or JPEG, up to 20 MB.">
          <input className={`${WL_INPUT} py-1.5`} type="file" aria-label="Signed ticket file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </WlField>
        <WlField label="Signed by *">
          <input className={WL_INPUT} value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
        </WlField>
        <WlField label="Day signed *">
          <input className={WL_DATE_INPUT} type="date" aria-label="Day signed" value={day} max={today ?? undefined} min={officeDay || undefined} onChange={(e) => setDay(e.target.value)} />
        </WlField>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WL_SECONDARY} onClick={() => setOpen(false)} disabled={record.isPending}>
          Cancel
        </button>
        <button type="button" className={WL_PRIMARY} onClick={submit} disabled={record.isPending}>
          {record.isPending ? 'Recording…' : 'Record approval'}
        </button>
      </div>
    </div>
  );
}

/** Who approved, when, how; the stamp; the stamped file. Read-only. */
export function WlApprovalRecord({ ticket, tz }: { ticket: WlTicket; tz: string | null }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const stamp = frozenStampFromTicket(ticket);
  const view = stamp ? frozenStampDisplay(stamp) : null;
  const source = ticket.customerApprovalSource ? WL_APPROVAL_SOURCE_TEXT[ticket.customerApprovalSource] ?? ticket.customerApprovalSource : null;

  const openFile = async () => {
    setError(null);
    const win = claimBlobTab();
    if (!win) return setError(POPUP_BLOCKED_MESSAGE);
    setBusy(true);
    try {
      const res = await api.wirelineSlickline.tickets.stampedFile(ticket.id);
      showBlobInTab(win, res.data);
    } catch (e) {
      win.close();
      setError(await getBlobApiErrorMessage(e, 'The signed copy could not be opened.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 px-4 py-4" data-testid="wl-ticket-approval-record">
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <WlSpec label="Approved by" value={ticket.customerApprovedByName || '—'} />
        <WlSpec label="Approved" value={at(ticket.customerApprovedAt, tz)} />
        <WlSpec label="How" value={source ?? '—'} />
        <WlSpec label="Recorded by" value={ticket.customerApprovalRecordedByName || '—'} />
      </dl>
      {view && (
        <div data-testid="wl-ticket-stamp" className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-4 py-3">
          <p className="text-sm font-extrabold" style={view.operatorColor ? { color: view.operatorColor } : undefined}>
            {view.operatorName}
          </p>
          <dl className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {view.rows.map((row, i) => (
              <WlSpec key={`${row.label}-${i}`} label={row.label} value={row.value} />
            ))}
          </dl>
        </div>
      )}
      {ticket.stampedFile && (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className={WL_SECONDARY} onClick={openFile} disabled={busy}>
            <FileText className="size-4" aria-hidden="true" />
            {busy ? 'Opening…' : 'Open the signed copy'}
          </button>
          <span className="text-xs text-fieldiq-muted">
            {ticket.stampedFile.filename} · uploaded {at(ticket.stampedFile.uploadedAt, tz)}
          </span>
        </div>
      )}
      <WlFormError message={error} />
    </div>
  );
}

/** The whole sign-off panel for one ticket. */
export function WlTicketSignOff({
  ticket,
  canWrite,
  tz,
  today,
  onUnpriced,
}: {
  ticket: WlTicket;
  canWrite: boolean;
  tz: string | null;
  today: string | null;
  onUnpriced: (ids: string[]) => void;
}) {
  const s = ticket.status;
  return (
    <WlPanel eyebrow="Sign-off" title="The office, then the company man" testId="wl-ticket-signoff">
      <dl className="grid gap-4 border-b border-fieldiq-border px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
        <WlSpec label="Office approved by" value={ticket.officeApprovedByName || '—'} />
        <WlSpec label="Office approved" value={at(ticket.officeApprovedAt, tz)} />
        <WlSpec label="Sent to" value={ticket.customerEmailSentTo || '—'} />
        <WlSpec label="Sent" value={at(ticket.sentToCustomerAt, tz)} />
        {ticket.approvalLinkExpiresAt && s === 'office_approved' && <WlSpec label="Link expires" value={at(ticket.approvalLinkExpiresAt, tz)} />}
        {ticket.customerEmailStatus && <WlSpec label="Email" value={EMAIL_STATUS_LABELS[ticket.customerEmailStatus] ?? ticket.customerEmailStatus} />}
      </dl>
      {ticket.customerEmailLastError && s === 'office_approved' && (
        <div className="px-4 pt-3">
          <WlFormError message={`The last email did not go through: ${ticket.customerEmailLastError}`} />
        </div>
      )}
      {s === 'customer_rejected' && (
        <div className="px-4 pt-4">
          <WlFormError message={`The customer rejected this ticket${ticket.customerRejectionReason ? `: “${ticket.customerRejectionReason}”` : '.'}`} />
        </div>
      )}
      {s === 'customer_approved' || (s === 'void' && ticket.customerApprovedAt) ? (
        <WlApprovalRecord ticket={ticket} tz={tz} />
      ) : s === 'void' ? (
        <div className="px-4 py-4">
          <WlNote>This ticket is void, so it is not approved or sent.</WlNote>
        </div>
      ) : !canWrite ? (
        <div className="px-4 py-4">
          <WlNote>Only the office (admin or office staff) approves and sends a ticket.</WlNote>
        </div>
      ) : s === 'draft' || s === 'customer_rejected' ? (
        <WlOfficeApprove ticket={ticket} onUnpriced={onUnpriced} />
      ) : s === 'office_approved' ? (
        <>
          <WlSendTicket ticket={ticket} tz={tz} />
          <WlRecordApproval ticket={ticket} tz={tz} today={today} />
        </>
      ) : null}
    </WlPanel>
  );
}
